import { signalrHub } from '@/plugins/signalr';
import {
    EnvelopeOfBoolean,
    IUserData,
    IEnvelopeOfUserData,
    PaymentTypes
} from '@/api/klip-api.proxy';
import { useCodeListStore } from './CodeListModule';
import userLocalStorage from '@/storage/user-local-storage';
import { useShoppingCartStore } from './ShoppingCartModule';
import { klipApiProxy } from '@/plugins/proxy-client';
import {useFeatureToggleStore} from '@/stores/feature-toggle-store';
import {defineStore} from 'pinia';
import {computed, ref, Ref} from 'vue';
import {isEmpty} from 'lodash-es';
import {EUserRole} from '@/app/shared/state/user-role';
import {useRouter} from '@/router/router-vue3';

export const useUserStore = defineStore('user-store', () => {

    const router = useRouter();

    const user: Ref<IUserData> = ref({});
    const scope: Ref<string> = ref('');

    const isUpdating: Ref<boolean> = ref(false);
    const isAuthenticated: Ref<boolean> = ref(false);
    const isCheckingAuthentication: Ref<boolean> = ref(true);

    const fullName = computed((): string => {
        let fullName = '';
        if (user.value.firstName) {
            fullName += user.value.firstName;
        }

        if (user.value.lastName) {
            if (user.value.firstName) {
                fullName += ' ';
            }
            fullName += user.value.lastName;
        }
        return fullName;
    });

    const loggedOn = computed((): boolean => {
        if (!user.value) {
            return false;
        }
        return !!user.value.klipUserId;
    });

    const applyVat = computed((): boolean => {
        return !!user.value && user.value.applyVat == true;
    });

    const vatPercentage = computed((): number => {
        return !!user.value && !!user.value.vatPercentage ? user.value.vatPercentage : 0;
    });

    const paymentType = computed((): PaymentTypes => {
        if (!user.value) {
            return PaymentTypes.None;
        }
        return user.value.paymentType;
    });


    const authenticated = computed((): boolean => {
        return isAuthenticated.value;
    });

    const scopes = computed(() => {
        const scopes = [];
        if (hasRole([EUserRole.una_manager, EUserRole.una_operator, EUserRole.una_reader, EUserRole.klim_admin])) {
            scopes.push({
                key: 'una',
                label: 'Kabel- en leidingbeheerder',
            });
        }
        if (hasRole([EUserRole.pda_manager])) {
            scopes.push({
                key: 'pda',
                label: 'Openbaar Domeinbeheerder',
            });
        }
        if (hasRole([EUserRole.mri, EUserRole.mrr])) {
            scopes.push({
                key: 'mri',
                label: 'Planaanvrager',
            });
        }
        if (hasRole([EUserRole.helpdesk, EUserRole.admin])) {
            scopes.push({
                key: 'admin',
                label: 'Contactpunt',
            });
        }
        if (hasRole([EUserRole.enforcer])) {
            scopes.push({
                key: 'enforcer',
                label: 'Handhaver',
            });
        }
        return scopes;
    });

    const userId = computed(() => {
        return user.value.klipUserId;
    });

    const organisationId = computed(() => {
        return user.value.klipOrganisationApiId;
    });

    const isContactpuntUser = computed((): boolean => {
        return user.value.isContactPunt || hasRole([EUserRole.helpdesk]) || hasRole([EUserRole.admin]);
    });


    const hasRole = (roles: EUserRole[]): boolean => {
        if (!isEmpty(roles)) {
            if (user.value && user.value.roles) {
                return _hasRole(user.value, roles);
            }
            return false;
        }
        return true;
    }

    const klipVisited = (): boolean => {
        return userLocalStorage.getItem('klip_visited');
    }

    const updateUserProfile = async (hardRefresh?: boolean) => {
        _updatingProfile(true);
        return klipApiProxy.acmIdmAuth_GetUserData(hardRefresh)
            .then(async (userDataEnvelope: IEnvelopeOfUserData) => {
                const userData = userDataEnvelope.result;
                userLocalStorage.setItem('klip_visited', true);
                _mutationUpdateUserProfile(userData);
                _signalrSetup(userData);
                await useFeatureToggleStore().fetch();
                await useCodeListStore().updateCodeList();
                _getScope(userData);
                await useShoppingCartStore().updateShoppingCart();
            }).finally(() => {
                _updatingProfile(false);
            });
    }

    const _signalrSetup = (userData: IUserData) => {
        if (signalrHub) {
            signalrHub.onConnected(() => {
                signalrHub.subscribeToContactNotifications(userData.klipUserId);
                if (!userData.isCitizen) {
                    signalrHub.subscribeToOrganisationNotifications(userData.klipOrganisationApiId);
                }
            });
        }
    }

    const isUserAuthenticated =  async (): Promise<boolean> => {
        updateIsCheckingAuthentication(true);
        return  klipApiProxy.acmIdmAuth_IsAuthenticated().then(async (isAuthenticated) => {
            if (isAuthenticated.result) {
                await signalrHub.start();
            }
            _updateIsAuthenticated(isAuthenticated.result);

            // TO VERIFY: remove all other calls to updateIsCheckingAuthentication(false)
            updateIsCheckingAuthentication(false);

            return Promise.resolve(isAuthenticated.result);
        });
    }

    const setShoppingCartInvisible = () => {
        user.value.isShoppingCartVisible = false;
    }

    const _mutationUpdateUserProfile = (userData: IUserData) => {
        user.value = userData;
    }

    const _hasRole = (userData: IUserData, roles: EUserRole[]): boolean => {
        return userData.roles.some((role) => {
            const eRole = EUserRole[role];
            console.assert(!!eRole, `_hasRole(${role}): unknown role '${role}'`);
            return roles.includes(eRole);
        });
    }

    const _getScope = (userData: IUserData) => {
        if (localStorage && userData && userData.klipUserId) {
            const scopeFromLocalStorage = userLocalStorage.getItem('scope');
            //check if the user still has the correct roles when loading the scope from the localstorage.
            switch (scopeFromLocalStorage) {
                case 'mri':
                    if (_hasRole(userData, [EUserRole.mri, EUserRole.mrr])) {
                        scope.value = scopeFromLocalStorage;
                    }
                    break;
                case 'una':
                    if (_hasRole(userData, [EUserRole.una_manager, EUserRole.una_operator, EUserRole.una_reader, EUserRole.klim_admin])) {
                        scope.value = scopeFromLocalStorage;
                    }
                    break;
                case 'pda':
                    if (_hasRole(userData, [EUserRole.pda_manager])) {
                        scope.value = scopeFromLocalStorage;
                    }
                    break;
                case 'admin':
                    if (_hasRole(userData, [EUserRole.helpdesk, EUserRole.admin])) {
                        scope.value = scopeFromLocalStorage;
                    }
                    break;
                case 'enforcer':
                    if (_hasRole(userData, [EUserRole.enforcer])) {
                        scope.value = scopeFromLocalStorage;
                    }
                    break;
            }
        }
        const determineUserScope = (userData: any): string => {
            if (_hasRole(userData, [EUserRole.mri, EUserRole.mrr])) {
                return 'mri';
            }
            if (_hasRole(userData, [EUserRole.una_manager, EUserRole.una_operator, EUserRole.una_reader, EUserRole.klim_admin])) {
                return 'una';
            }
            if (_hasRole(userData, [EUserRole.pda_manager])) {
                return 'pda';
            }
            if (_hasRole(userData, [EUserRole.helpdesk, EUserRole.admin])) {
                return 'admin';
            }
            if (_hasRole(userData, [EUserRole.enforcer])) {
                return 'enforcer';
            }
            return '';
        };

        if (!scope.value) {
            scope.value = determineUserScope(userData);
        }
    }

    const setScope = (newScope: string) => {
        if (localStorage && user.value && user.value.klipUserId) {
            userLocalStorage.setItem('scope', newScope);
        }
        scope.value = newScope;
    }

    const _updatingProfile = (updating: boolean) => {
        isUpdating.value = updating;
    }

    // TODO: plaats waar dit gebruikt wordt mag verhuizen naar deze store?
    const updateIsCheckingAuthentication = (isChecking: boolean) => {
        isCheckingAuthentication.value = isChecking;
    }

    const _updateIsAuthenticated = (authenticated: boolean) => {
        isAuthenticated.value = authenticated;
    }

    const cleanProfile = (redirect = true) => {
        user.value = {};
        if (redirect) {
            router.push('/');
        }
    }

    const $mockUser = (userData: IUserData) => {
        _mutationUpdateUserProfile(userData);
    }

    const $mockIsAuthenticated = (authenticated: boolean) => {
        _updateIsAuthenticated(authenticated);
    }


    return {
        user,
        scope,
        isUpdating,
        isAuthenticated,
        isCheckingAuthentication,

        fullName,
        loggedOn,
        applyVat,
        vatPercentage,
        paymentType,
        authenticated,
        scopes,
        userId,
        organisationId,
        isContactpuntUser,

        klipVisited,

        hasRole,

        updateUserProfile,
        isUserAuthenticated,
        setShoppingCartInvisible,
        setScope,
        cleanProfile,

        updateIsCheckingAuthentication,

        $mockUser,
        $mockIsAuthenticated,
    }
});
